/**
 * Account Manager Utility
 * Handles linked accounts from Cognito custom attributes
 */

/**
 * Retrieves linked accounts from Cognito user attributes
 * @param {Object} userAttributes - Cognito user attributes
 * @param {string} fallbackEmail - Email to use if no linked accounts configured
 * @returns {Array} Array of account objects with account_name, display_name, is_default
 */
export function getLinkedAccounts(userAttributes, fallbackEmail) {
  const linkedAccountsAttr = userAttributes?.['custom:linked_accounts'];

  if (!linkedAccountsAttr) {
    // Default: single account using email
    return [{
      account_name: fallbackEmail,
      display_name: "",
      is_default: true
    }];
  }

  try {
    const accounts = JSON.parse(linkedAccountsAttr);

    // Validate it's an array
    if (!Array.isArray(accounts) || accounts.length === 0) {
      throw new Error('Invalid accounts format');
    }

    // Ensure at least one default exists
    if (!accounts.some(a => a.is_default)) {
      accounts[0].is_default = true;
    }

    return accounts;
  } catch (e) {
    console.error('Failed to parse linked accounts:', e);
    // Fallback to single account
    return [{
      account_name: fallbackEmail,
      display_name: "",
      is_default: true
    }];
  }
}

/**
 * Gets the default account from a list of linked accounts
 * @param {Array} accounts - Array of account objects
 * @returns {Object} The default account object
 */
export function getDefaultAccount(accounts) {
  return accounts.find(a => a.is_default) || accounts[0];
}

/**
 * Gets the currently selected account from storage or returns default
 * @param {Array} accounts - Array of account objects
 * @returns {Object} The selected account object
 */
export function getCurrentAccount(accounts) {
  const storedAccountName = localStorage.getItem('habitaio_selected_account');

  if (storedAccountName) {
    const found = accounts.find(a => a.account_name === storedAccountName);
    if (found) return found;
  }

  return getDefaultAccount(accounts);
}

/**
 * Sets the currently selected account in storage
 * @param {string} accountName - The account_name to select
 */
export function setCurrentAccount(accountName) {
  localStorage.setItem('habitaio_selected_account', accountName);
}
